// 导入 zzshu 商品到 fuelio products 的 modal 编辑器。
//
// 流程：admin 在 DataSyncZzshu 表格点"导入"→ 本 modal 加载该商品全字段
// → 预填 name/cover/price/markdown，admin 美化文案 → 保存。保存成功后：
//   1. fuelio.products 新增一行（含 description / stock / 价格 / 封面）
//   2. fuelio-crawler-db.data_sync_links 建立 zzshu#id → fuelio.id 映射
// 后续 zzshu 上游变动只通过"更新"按钮把 price/stock 拉过来，description /
// name / cover 由 admin 控制。
//
// SKU 默认 ZZSHU-${id}；admin 可改。保存前不主动查重 —— 用户提交时由后端
// 拒（409 sku_taken），错误反馈会回显到 SKU 输入框上方。

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  api, errorMessage,
  type ZzshuCommodityFullRow, type DataSyncImportInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useToast } from '@/components/ui/Toast'
import { MarkdownPaneEditor } from '@/components/MarkdownPaneEditor'
import { PageError, PageLoading } from './Stats'

type Form = Omit<DataSyncImportInput, 'commodityId'>

const TYPE_OPTIONS = [
  { value: 'physical' as const, label: '实物 / 账号 (physical)' },
  { value: 'plan'     as const, label: '订阅 / 套餐 (plan)' },
  { value: 'topup'    as const, label: '代充 (topup)' },
]

export function DataSyncImportModal({
  commodityId,
  onClose,
  onSuccess,
}: {
  commodityId: number
  onClose: () => void
  onSuccess: () => void
}) {
  const toast = useToast()
  const [src, setSrc] = useState<ZzshuCommodityFullRow | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // 加载源数据 + 用它预填 form
  useEffect(() => {
    let alive = true
    api.dataSyncZzshuCommodity(commodityId)
      .then((r) => {
        if (!alive) return
        const c = r.commodity
        setSrc(c)
        setForm({
          sku: `ZZSHU-${c.id}`,
          type: 'physical',
          nameZh: c.name,
          nameEn: '',
          priceCnyCents: c.price_cents,
          priceUsdCents: null,
          descriptionMd: c.description_md ?? '',
          active: true,
          autoSyncPrice: true,
          autoSyncStock: true,
          coverUrl: c.cover ?? undefined,
        })
      })
      .catch((e) => { if (alive) setLoadErr(errorMessage(e)) })
    return () => { alive = false }
  }, [commodityId])

  if (loadErr) return (
    <ModalShell onClose={onClose} title={`导入 zzshu#${commodityId}`}>
      <PageError msg={loadErr}/>
    </ModalShell>
  )
  if (!src || !form) return (
    <ModalShell onClose={onClose} title={`导入 zzshu#${commodityId}`}>
      <PageLoading/>
    </ModalShell>
  )

  function update<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((prev) => prev ? { ...prev, [k]: v } : prev)
  }

  async function submit() {
    if (!form) return
    setBusy(true); setErr(null)
    try {
      const r = await api.dataSyncZzshuImport({ commodityId, ...form })
      toast.success(`已导入 ${r.sku}`)
      onSuccess()
    } catch (e) {
      // SKU 冲突 / 已链接 / 校验失败等都从这里冒出来
      setErr(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell onClose={onClose} title={`导入 zzshu#${commodityId} → fuelio products`}>
      {err && (
        <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-4">
        {/* 主图预览 */}
        <div>
          {form.coverUrl ? (
            <img src={form.coverUrl} alt="" className="w-40 h-40 object-cover rounded border border-border"/>
          ) : (
            <div className="w-40 h-40 rounded border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">无封面</div>
          )}
          <Input
            value={form.coverUrl ?? ''}
            onChange={(e) => update('coverUrl', e.target.value || undefined)}
            placeholder="封面 URL"
            className="mt-2 text-xs"
          />
          <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
            默认 hot-link 上游 CDN。生产建议手动改成 fuelio R2 的 URL。
          </p>
        </div>

        {/* 字段 */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="SKU（唯一）" hint="大写字母/数字/_/-">
              <Input
                value={form.sku}
                onChange={(e) => update('sku', e.target.value.toUpperCase())}
                className="font-mono"
              />
            </Field>
            <Field label="商品类型">
              <select
                value={form.type}
                onChange={(e) => update('type', e.target.value as Form['type'])}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
              >
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="名称 (中文)" hint="客户站默认显示这个">
            <Input value={form.nameZh} onChange={(e) => update('nameZh', e.target.value)}/>
          </Field>
          <Field label="名称 (英文)" hint="留空则 en 站回退用中文">
            <Input value={form.nameEn ?? ''} onChange={(e) => update('nameEn', e.target.value)}/>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="价格 CNY (元)"
              hint={`上游原价 ¥${(src.price_cents / 100).toFixed(2)} ｜ 内部存为 ${form.priceCnyCents} 分`}
            >
              {/* 输入用元（带 2 位小数）；form 状态 / API 提交都是 cents。
                  避免管理员心算 12500 / 100 那种磨人的"分/元换算"。 */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">¥</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={(form.priceCnyCents / 100).toString()}
                  onChange={(e) => {
                    const yuan = parseFloat(e.target.value || '0')
                    if (Number.isFinite(yuan)) update('priceCnyCents', Math.max(0, Math.round(yuan * 100)))
                  }}
                  className="font-mono pl-7"
                />
              </div>
            </Field>
            <Field label="价格 USD (元)" hint="可选；跨币种售卖时填，留空 = 不设 USD 价">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceUsdCents !== null && form.priceUsdCents !== undefined ? (form.priceUsdCents / 100).toString() : ''}
                  onChange={(e) => {
                    if (e.target.value === '') return update('priceUsdCents', null)
                    const dollar = parseFloat(e.target.value)
                    if (Number.isFinite(dollar)) update('priceUsdCents', Math.max(0, Math.round(dollar * 100)))
                  }}
                  placeholder="—"
                  className="font-mono pl-7"
                />
              </div>
            </Field>
          </div>

          <MarkdownPaneEditor
            label="宝贝详情"
            value={form.descriptionMd}
            onChange={(v) => update('descriptionMd', v)}
            rows={14}
            fixedHeight={360}
            initialMode="split"
            hint={`从上游 HTML 自动转换 · ${form.descriptionMd.length} 字`}
            placeholder="# 标题…"
          />

          <div className="flex flex-wrap gap-4 items-center">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => update('active', e.target.checked)}
              />
              立即上架 (active)
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.autoSyncPrice}
                onChange={(e) => update('autoSyncPrice', e.target.checked)}
              />
              后续"更新"覆盖价格
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.autoSyncStock}
                onChange={(e) => update('autoSyncStock', e.target.checked)}
              />
              后续"更新"覆盖库存
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-3 mt-2">
            <div className="text-xs text-muted-foreground">
              当前上游库存 <span className="font-mono">{src.stock}</span>，导入后会写到 metadata.stock。
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>取消</Button>
              <Button onClick={() => void submit()} disabled={busy}>
                {busy ? '保存中…' : '保存到 fuelio products'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs font-medium text-foreground">{label}</label>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  )
}

function ModalShell({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold text-sm">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
